/**
 * Frontend scope permission helpers.
 *
 * SCOPE_ALIAS_MAP: maps each specific scope to parent/legacy scopes that also grant it.
 * This mirrors the backend's RequireOauthScope OR logic and supports legacy manage scopes.
 *
 * When adding a new module:
 *   1. Add scope_id entries to SCOPE_ALIAS_MAP for each action
 *   2. Include the .manage alias and system:users-management as fallbacks
 */

const SCOPE_ALIAS_MAP = {
  'dashboard.view': ['system:users-management'],

  'oauth-users.view':               ['oauth-users.manage', 'system:users-management'],
  'oauth-users.create':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.update':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.delete':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.permissions.view':   ['oauth-users.manage', 'system:users-management', 'system:scope-management'],
  'oauth-users.permissions.update': ['oauth-users.manage', 'system:users-management', 'system:scope-management'],

  // ── MedTransact business modules ───────────────────────────────────────
  'providers.view':   ['providers.manage', 'system:users-management'],
  'providers.create': ['providers.manage', 'system:users-management'],
  'providers.update': ['providers.manage', 'system:users-management'],
  'providers.delete': ['providers.manage', 'system:users-management'],

  'diagnoses.view':   ['diagnoses.manage', 'system:users-management'],
  'diagnoses.create': ['diagnoses.manage', 'system:users-management'],
  'diagnoses.update': ['diagnoses.manage', 'system:users-management'],
  'diagnoses.delete': ['diagnoses.manage', 'system:users-management'],

  'cpts.view':   ['cpts.manage', 'system:users-management'],
  'cpts.create': ['cpts.manage', 'system:users-management'],
  'cpts.update': ['cpts.manage', 'system:users-management'],
  'cpts.delete': ['cpts.manage', 'system:users-management'],

  'physicians.view':   ['physicians.manage', 'system:users-management'],
  'physicians.create': ['physicians.manage', 'system:users-management'],
  'physicians.update': ['physicians.manage', 'system:users-management'],
  'physicians.delete': ['physicians.manage', 'system:users-management'],

  'corporates.view':   ['corporates.manage', 'system:users-management'],
  'corporates.create': ['corporates.manage', 'system:users-management'],
  'corporates.update': ['corporates.manage', 'system:users-management'],
  'corporates.delete': ['corporates.manage', 'system:users-management'],

  'plan-setups.view':   ['plan-setups.manage', 'system:users-management'],
  'plan-setups.create': ['plan-setups.manage', 'system:users-management'],
  'plan-setups.update': ['plan-setups.manage', 'system:users-management'],
  'plan-setups.delete': ['plan-setups.manage', 'system:users-management'],

  'members.view':   ['members.manage', 'system:users-management'],
  'members.create': ['members.manage', 'system:users-management'],
  'members.update': ['members.manage', 'system:users-management'],
  'members.delete': ['members.manage', 'system:users-management'],
  'members.loa':    ['members.manage', 'system:users-management'],

  'payments.view':   ['payments.manage', 'system:users-management'],
  'payments.update': ['payments.manage', 'system:users-management'],
  'payments.export': ['payments.manage', 'system:users-management'],

  'mis-reports.view':   ['mis-reports.manage', 'system:users-management'],
  'mis-reports.export': ['mis-reports.manage', 'system:users-management'],

  'oauth-clients.view':          ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.create':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.update':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.delete':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.rotate-secret': ['oauth-clients.manage', 'system:users-management'],

  'upload-s3-public-share-token.view':       ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.update':     ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.regenerate': ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.toggle':     ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.export':     ['upload-s3-public-share-token.manage', 'system:users-management'],

  // NOTE: example-batch-upload / chat / calls / bloom-* scope aliases removed
  // (those modules moved to backup-not-used — not used by MedTransact).

  'notifications.view': ['notifications.manage', 'system:users-management'],

  // Authorized Devices (Rule 73)
  'device-registration.view':   ['device-registration.delete', 'system:users-management'],
  'device-registration.delete': ['system:users-management'],

  // System Settings (Rule 74 — Phase 1 settings stack)
  'system-settings.view':   ['system-settings.manage', 'system:users-management'],
  'system-settings.update': ['system-settings.manage', 'system:users-management'],

  // System Administrator read-only viewers (Rule 68; Rule 65 system:* exception)
  'admin-audit-logs.view':          ['system:users-management'],
  'admin-audit-logs.export':        ['system:users-management'],
  'admin-outbound-http-logs.view':  ['system:users-management'],
  'admin-field-history.view':       ['system:users-management'],
  'admin-login-attempts.view':      ['system:users-management'],
  'admin-webhook-deliveries.view':  ['system:users-management'],
  'admin-failed-jobs.view':         ['system:users-management'],
  'admin-failed-jobs.retry':        ['system:users-management'],

  // ── Add new module scopes here ─────────────────────────────────────────────
  // Pattern:
  // '{module-key}.view':   ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.create': ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.update': ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.delete': ['{module-key}.manage', 'system:users-management'],
};

/**
 * Returns all scope IDs that grant a given required scope
 * (the scope itself plus all its aliases).
 */
function grantedScopesFor(requiredScope) {
  return [requiredScope, ...(SCOPE_ALIAS_MAP[requiredScope] ?? [])];
}

/**
 * Normalize a raw scope value from the API to a plain string array.
 */
export function normalizeScopeList(scopes) {
  return Array.isArray(scopes) ? scopes : [];
}

/**
 * Returns true if userScopes satisfies requiredScope (including aliases).
 */
export function hasScope(userScopes, requiredScope) {
  return grantedScopesFor(requiredScope).some((s) => userScopes.includes(s));
}

/**
 * Returns true if userScopes satisfies ANY scope in requiredScopes (OR logic).
 */
export function hasAnyScope(userScopes, requiredScopes) {
  return requiredScopes.some((s) => hasScope(userScopes, s));
}
